'use client'

import { useState, useRef } from 'react'

interface ShoppingItem {
  name: string
  quantity: number
}

interface MatchedProduct {
  original_name: string
  matched_name: string
  price: number
  unit: string
  match_score: number
  subtotal: number
  price_per_kg: number | null
  reference_unit: string | null
}

interface SupermarketResult {
  supermarket_id: string
  supermarket_name: string
  items: MatchedProduct[]
  unmatched: string[]
  total: number
}

interface CompareResponse {
  shopping_list: string[]
  results: SupermarketResult[]
}

const SUPERMARKET_EMOJI: Record<string, string> = {
  mercadona: '🟢',
  carrefour: '🔵',
  dia: '🔴',
  alcampo: '🟠',
}

export default function ShoppingListComparator() {
  const [items, setItems] = useState<ShoppingItem[]>([{ name: '', quantity: 1 }])
  const [results, setResults] = useState<CompareResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const lastInputRef = useRef<HTMLInputElement>(null)

  const addItem = () => {
    setItems(prev => [...prev, { name: '', quantity: 1 }])
    setTimeout(() => lastInputRef.current?.focus(), 50)
  }

  const removeItem = (i: number) =>
    setItems(prev => prev.filter((_, idx) => idx !== i))

  const updateName = (i: number, value: string) =>
    setItems(prev => prev.map((it, idx) => idx === i ? { ...it, name: value } : it))

  const updateQty = (i: number, value: number) =>
    setItems(prev => prev.map((it, idx) => idx === i ? { ...it, quantity: Math.max(1, value) } : it))

  const compare = async () => {
    const valid = items.filter(i => i.name.trim())
    if (!valid.length) return
    setLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/compare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(valid.map(i => ({ name: i.name.trim(), quantity: i.quantity }))),
      })
      if (!res.ok) throw new Error(`Error ${res.status}`)
      setResults(await res.json())
    } catch (e) {
      setError('No se pudo conectar con el servidor. Inténtalo de nuevo.')
    } finally {
      setLoading(false)
    }
  }

  const savings = results && results.results.length > 1
    ? results.results[results.results.length - 1].total - results.results[0].total
    : 0

  return (
    <div className="space-y-6">
      {/* Input card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h2 className="font-semibold text-gray-700 mb-4">¿Qué necesitas comprar?</h2>

        <div className="space-y-2">
          {items.map((item, i) => (
            <div key={i} className="flex gap-2 items-center group">
              <input
                ref={i === items.length - 1 ? lastInputRef : undefined}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 transition"
                placeholder={i === 0 ? 'Ej: leche entera, aceite de oliva…' : 'Otro producto…'}
                value={item.name}
                onChange={e => updateName(i, e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addItem() }}
              />
              <input
                type="number"
                min={1}
                className="w-14 border border-gray-200 rounded-lg px-2 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-green-400 transition"
                value={item.quantity}
                onChange={e => updateQty(i, Number(e.target.value))}
              />
              <button
                onClick={() => removeItem(i)}
                className={`text-gray-300 hover:text-red-400 transition px-1 ${items.length === 1 ? 'invisible' : ''}`}
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <button
          onClick={addItem}
          className="mt-3 text-sm text-green-600 hover:text-green-700 font-medium"
        >
          + Añadir producto
        </button>

        {error && <p className="mt-3 text-sm text-red-500">{error}</p>}

        <button
          onClick={compare}
          disabled={loading || !items.some(i => i.name.trim())}
          className="mt-5 w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white font-semibold py-3 rounded-xl transition text-sm"
        >
          {loading ? 'Buscando precios…' : 'Comparar precios'}
        </button>
      </div>

      {/* Savings banner */}
      {results && savings > 0.01 && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-5 py-3 flex items-center gap-3">
          <span className="text-2xl">💡</span>
          <p className="text-sm text-green-800">
            Comprando en <strong>{results.results[0].supermarket_name}</strong> ahorras{' '}
            <strong>{savings.toFixed(2)}€</strong> frente a la opción más cara.
          </p>
        </div>
      )}

      {/* Results */}
      {results && results.results.map((r, rank) => (
        <div
          key={r.supermarket_id}
          className={`bg-white rounded-2xl shadow-sm border p-6 ${rank === 0 ? 'border-green-400 ring-1 ring-green-300' : 'border-gray-100'}`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-lg">{SUPERMARKET_EMOJI[r.supermarket_id] ?? '🏪'}</span>
              <h3 className="font-bold text-lg">{r.supermarket_name}</h3>
              {rank === 0 && (
                <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full">
                  MÁS BARATO
                </span>
              )}
            </div>
            <span className="text-2xl font-bold">{r.total.toFixed(2)}€</span>
          </div>

          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-400 uppercase border-b">
                <th className="text-left pb-2 font-medium">Buscado</th>
                <th className="text-left pb-2 font-medium">Encontrado</th>
                <th className="text-right pb-2 font-medium">Precio unit.</th>
                <th className="text-right pb-2 font-medium">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {r.items.map((item, j) => (
                <tr key={j}>
                  <td className="py-2 text-gray-500 pr-3 whitespace-nowrap">{item.original_name}</td>
                  <td className="py-2 pr-3">
                    <span className="text-gray-800">{item.matched_name}</span>
                    <span
                      className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full font-medium ${
                        item.match_score >= 80 ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'
                      }`}
                    >
                      {item.match_score}%
                    </span>
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <div className="font-medium">
                      {item.price.toFixed(2)}€
                      <span className="text-gray-400 text-xs font-normal ml-0.5">/{item.unit}</span>
                    </div>
                    {item.price_per_kg != null && item.reference_unit && (
                      <div className="text-xs text-gray-400">
                        {item.price_per_kg.toFixed(2)}€/{item.reference_unit}
                      </div>
                    )}
                  </td>
                  <td className="py-2 text-right font-semibold whitespace-nowrap">{item.subtotal.toFixed(2)}€</td>
                </tr>
              ))}
              {r.unmatched.map((name, j) => (
                <tr key={`u-${j}`} className="opacity-50">
                  <td className="py-2 text-gray-500 pr-3">{name}</td>
                  <td className="py-2 italic text-gray-400" colSpan={3}>Sin resultado</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  )
}
